import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import { DollarSign, Plus, Trash2, Calendar, User, CreditCard, Home, ArrowLeft } from "lucide-react";
import Card from "../../components/Card";

interface SubscriptionPayment {
  id: string;
  instructorId: string;
  instructorEmail: string;
  instructorName: string;
  tier: string;
  amount: number;
  paymentDate: Date;
  paymentMethod: string;
  note?: string;
  createdAt: any;
}

export default function SubscriptionPayments() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState<SubscriptionPayment[]>([]);
  const [instructors, setInstructors] = useState<any[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    instructorId: "",
    tier: "pro",
    amount: 0,
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: "bank_transfer",
    note: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.email !== "viitamint@gmail.com") {
      navigate("/");
      return;
    }

    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      // Load all instructors
      const usersSnap = await getDocs(collection(db, "users"));
      const instructorsList = usersSnap.docs
        .map(doc => ({ uid: doc.id, ...doc.data() }))
        .filter((u: any) => u.role === "instructor" || u.role === "admin");
      setInstructors(instructorsList);

      // Load all subscription payments
      const paymentsQuery = query(
        collection(db, "subscription_payments"),
        orderBy("paymentDate", "desc")
      );
      const paymentsSnap = await getDocs(paymentsQuery);
      const paymentsList = paymentsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        paymentDate: doc.data().paymentDate?.toDate() || new Date()
      })) as SubscriptionPayment[];

      setPayments(paymentsList);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.instructorId) {
      alert("กรุณาเลือกผู้สอน");
      return;
    }

    setSaving(true);
    try {
      const instructor = instructors.find(i => i.uid === formData.instructorId);
      
      await addDoc(collection(db, "subscription_payments"), {
        instructorId: formData.instructorId,
        instructorEmail: instructor?.email || "",
        instructorName: instructor?.name || "",
        tier: formData.tier,
        amount: Number(formData.amount),
        paymentDate: new Date(formData.paymentDate),
        paymentMethod: formData.paymentMethod,
        note: formData.note,
        status: "pending",
        createdAt: serverTimestamp()
      });

      alert("✅ บันทึกการชำระเงินสำเร็จ");
      setShowAddForm(false);
      setFormData({
        instructorId: "",
        tier: "pro",
        amount: 0,
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: "bank_transfer",
        note: ""
      });
      loadData();
    } catch (error) {
      console.error("Error adding payment:", error);
      alert("เกิดข้อผิดพลาด: " + error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!confirm("ต้องการลบรายการชำระเงินนี้?")) return;

    try {
      await deleteDoc(doc(db, "subscription_payments", paymentId));
      alert("✅ ลบรายการสำเร็จ");
      loadData();
    } catch (error) {
      console.error("Error deleting payment:", error);
      alert("เกิดข้อผิดพลาด: " + error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const getTierLabel = (tier: string) => {
    const labels: Record<string, string> = {
      free: "FREE",
      start: "",
      pro: "PRO",
      yearly: "Yearly"
    };
    return labels[tier] || tier.toUpperCase();
  };

  const getTierColor = (tier: string) => {
    if (tier === "yearly") return "bg-yellow-100 text-yellow-700";
    if (tier === "pro_plus") return "bg-pink-100 text-pink-700";
    if (tier === "pro") return "bg-blue-100 text-blue-700";
    return "bg-gray-100 text-gray-700";
  };

  const totalRevenue = payments.reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div>
                <h1 className="text-4xl font-bold text-textDark">จัดการการชำระเงิน Subscription</h1>
                <p className="text-textLight">บันทึกและติดตามการชำระเงินของผู้สอน</p>
              </div>
            </div>
            <Link to="/">
              <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                <Home className="w-5 h-5" />
                <span>ไปหน้าแรก</span>
              </button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                  <DollarSign className="w-8 h-8 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">รายได้รวม</p>
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <CreditCard className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">จำนวนรายการ</p>
                  <p className="text-3xl font-bold text-textDark">{payments.length}</p>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <User className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-textLight">ผู้สอนที่ชำระแล้ว</p>
                  <p className="text-3xl font-bold text-textDark">
                    {new Set(payments.map(p => p.instructorId)).size}
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* Add Button */}
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-2 bg-gradient-primary text-white px-6 py-3 rounded-xl font-semibold hover:shadow-xl transition-all"
          >
            <Plus className="w-5 h-5" />
            เพิ่มรายการชำระเงิน
          </button>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <Card className="mb-8">
            <form onSubmit={handleAddPayment} className="space-y-6">
              <h3 className="text-2xl font-bold text-textDark mb-4">เพิ่มรายการชำระเงิน</h3>

              {/* Instructor Selection */}
              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  เลือกผู้สอน *
                </label>
                <select
                  value={formData.instructorId}
                  onChange={(e) => setFormData({ ...formData, instructorId: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  required
                >
                  <option value="">-- เลือกผู้สอน --</option>
                  {instructors.map(instructor => (
                    <option key={instructor.uid} value={instructor.uid}>
                      {instructor.name || instructor.email} ({instructor.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Tier Selection */}
              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  แพ็กเกจ *
                </label>
                <select
                  value={formData.tier}
                  onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  required
                >
                  <option value="">START</option>
                  <option value="pro">PRO</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  จำนวนเงิน (บาท) *
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  required
                  min="0"
                  step="0.01"
                />
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  วันที่ชำระเงิน *
                </label>
                <input
                  type="date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  required
                />
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  วิธีการชำระเงิน *
                </label>
                <select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  required
                >
                  <option value="bank_transfer">โอนเงินผ่านธนาคาร</option>
                  <option value="promptpay">พร้อมเพย์</option>
                  <option value="credit_card">บัตรเครดิต</option>
                  <option value="cash">เงินสด</option>
                  <option value="other">อื่นๆ</option>
                </select>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-semibold text-textDark mb-2">
                  หมายเหตุ
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  rows={3}
                  placeholder="เพิ่มหมายเหตุ (ถ้ามี)"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-gradient-primary text-white py-3 rounded-xl font-bold hover:shadow-xl transition-all disabled:opacity-50"
                >
                  {saving ? "กำลังบันทึก..." : "บันทึกรายการ"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-6 py-3 bg-gray-200 text-textDark rounded-xl font-semibold hover:bg-gray-300 transition-all"
                >
                  ยกเลิก
                </button>
              </div>
            </form>
          </Card>
        )}

        {/* Payments List */}
        <Card>
          <h3 className="text-2xl font-bold text-textDark mb-6">รายการชำระเงินทั้งหมด</h3>

          {payments.length === 0 ? (
            <div className="text-center py-12">
              <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-textLight">ยังไม่มีรายการชำระเงิน</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-textDark">วันที่</th>
                    <th className="text-left py-3 px-4 font-semibold text-textDark">ผู้สอน</th>
                    <th className="text-left py-3 px-4 font-semibold text-textDark">แพ็กเกจ</th>
                    <th className="text-left py-3 px-4 font-semibold text-textDark">จำนวนเงิน</th>
                    <th className="text-left py-3 px-4 font-semibold text-textDark">วิธีชำระ</th>
                    <th className="text-left py-3 px-4 font-semibold text-textDark">หมายเหตุ</th>
                    <th className="text-center py-3 px-4 font-semibold text-textDark">การกระทำ</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2 text-textLight">
                          <Calendar className="w-4 h-4" />
                          <span className="text-sm">{formatDate(payment.paymentDate)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-semibold text-textDark">{payment.instructorName}</p>
                          <p className="text-xs text-textLight">{payment.instructorEmail}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getTierColor(payment.tier)}`}>
                          {getTierLabel(payment.tier)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-bold text-green-600">{formatCurrency(payment.amount)}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-textLight">
                        {payment.paymentMethod === "bank_transfer" && "โอนธนาคาร"}
                        {payment.paymentMethod === "promptpay" && "พร้อมเพย์"}
                        {payment.paymentMethod === "credit_card" && "บัตรเครดิต"}
                        {payment.paymentMethod === "cash" && "เงินสด"}
                        {payment.paymentMethod === "other" && "อื่นๆ"}
                      </td>
                      <td className="py-3 px-4 text-sm text-textLight">
                        {payment.note || "-"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => handleDeletePayment(payment.id)}
                          className="text-red-600 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-all"
                          title="ลบรายการ"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
